// ClientPortalsPage — Phase 1 cleanup — portal-enabled clients overview.
import { useCallback, useEffect, useState } from "react";
import { Loader2, ExternalLink, Users, UserCheck, Palette, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";
import ClientFormDialog from "@/components/ClientFormDialog";
import { useAuth } from "@/context/AuthContext";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);

export default function ClientPortalsPage() {
    const [clients, setClients] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const { user } = useAuth();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);

    const refresh = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get("/clients"),
            api.get("/contacts").catch(() => ({ data: [] })),
        ]).then(([cs, ct]) => { setClients(cs.data); setContacts(ct.data); })
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Cannot load"))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    if (loading) return <div className="text-center py-10 text-[#9A968F]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>;

    return (
        <div data-testid="client-portals-page" className="space-y-5">
            <div>
                <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                    style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>Clients &amp; Portals</span>
                <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Client Portals</h2>
                <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                    {clients.length} clients · {contacts.filter((c) => c.portal_access).length} portal logins active.
                    Open a portal to preview what the client sees with their branding.
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {clients.map((c) => {
                    const portalContacts = contacts.filter((ct) => ct.client_id === c.id && ct.portal_access);
                    const allContacts = contacts.filter((ct) => ct.client_id === c.id);
                    return (
                        <PortalCard key={c.id} c={c}
                            portalUsers={portalContacts.length}
                            contactsCount={allContacts.length}
                            canEdit={canEdit}
                            onEdit={() => setEditing(c)} />
                    );
                })}
            </div>

            {editing && (
                <ClientFormDialog open mode="edit" initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); refresh(); }} />
            )}
        </div>
    );
}

function PortalCard({ c, portalUsers, contactsCount, canEdit, onEdit }) {
    const [logoBroken, setLogoBroken] = useState(false);
    const primary = c.branding?.primary_colour || "var(--brand-accent, #C75A1B)";
    const secondary = c.branding?.secondary_colour || null;
    // Solid client primary header (secondary stays an accent only — no blue→yellow gradient)
    const headerBg = primary;
    const logoSrc = c.branding?.logo_url && !logoBroken
        ? `${process.env.REACT_APP_BACKEND_URL}/api/clients/${c.id}/logo`
        : null;
    return (
        <div data-testid={`portal-card-${c.id}`}
            className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden relative group shadow-sm hover:shadow-md transition-shadow">
            <div className="h-16 relative" style={{ background: headerBg }}>
                {logoSrc ? (
                    <img src={logoSrc} alt="" onError={() => setLogoBroken(true)}
                        className="absolute bottom-1 right-3 h-12 max-w-[120px] object-contain bg-white rounded p-0.5 shadow-sm" />
                ) : (
                    <span className="absolute bottom-1 right-3 grid place-items-center w-12 h-12 rounded bg-white/95 font-bold text-lg shadow-sm" style={{ color: primary }}>
                        {c.branding?.logo_initials || c.name.slice(0, 2).toUpperCase()}
                    </span>
                )}
                {canEdit && (
                    <button onClick={onEdit}
                        data-testid={`portal-edit-${c.id}`}
                        title="Edit client + branding"
                        className="absolute top-2 left-2 inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-md bg-white text-[#1F1F1F] text-[11px] font-semibold shadow-sm hover:bg-[#F6F4F0] transition-colors">
                        <Pencil size={12} /> Edit
                    </button>
                )}
            </div>
            <div className="px-4 py-4 space-y-3">
                <div>
                    <div className="font-bold text-[#1F1F1F]">{c.name}</div>
                    <div className="text-[11px] text-[#9A968F]">/{c.slug}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Pill tone={c.status === "Active" ? "green" : "amber"}>{c.status || "Active"}</Pill>
                    <Pill tone={c.bsc_enabled ? "blue" : "neutral"}>BSC {c.bsc_enabled ? "on" : "off"}</Pill>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-[#F0EDE8]">
                    <Stat icon={Users} label="Contacts" value={contactsCount} />
                    <Stat icon={UserCheck} label="Portal users" value={portalUsers} />
                    <Stat icon={Palette} label="Brand" value={
                        <span className="inline-flex items-center justify-center gap-0.5">
                            <span className="inline-block w-4 h-4 rounded-full border border-[#E5E0DA]" style={{ background: primary }} />
                            {secondary && (
                                <span className="inline-block w-4 h-4 rounded-full border border-[#E5E0DA]" style={{ background: secondary }} />
                            )}
                        </span>
                    } />
                </div>
                <a href={`/c/${c.slug}/dashboard`} target="_blank" rel="noreferrer"
                    data-testid={`portal-open-${c.id}`}
                    className="block text-center text-[12px] font-semibold py-2 rounded-lg text-white"
                    style={{ background: headerBg }}>
                    Open portal <ExternalLink size={11} className="inline ml-1" />
                </a>
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div>
            <Icon size={12} className="inline text-[#9A968F]" />
            <div className="text-base font-bold text-[#1F1F1F]">{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#9A968F]">{label}</div>
        </div>
    );
}
